import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, trackEvent } from "@/lib/api";
import { CATEGORIES, CATEGORY_META } from "@/lib/categories";
import SystemCard from "@/components/SystemCard";
import ContactRequestDialog from "@/components/ContactRequestDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Sparkles, ArrowRight, ShieldCheck, Zap, LayoutGrid } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/a73c5dac-2550-46df-84c2-e7617567716a/images/4740ced38085b8fd2832eb812f2273378d7e57f2f1bca4a88f98f7f5fe395bc8.png";

export default function Home() {
  const [systems, setSystems] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    trackEvent("page_view", { path: "/" });
    api.get("/systems").then((r) => setSystems(r.data));
  }, []);

  const featured = useMemo(() => systems.filter((s) => s.is_featured).slice(0, 3), [systems]);
  const filtered = useMemo(() => {
    return systems.filter((s) => {
      if (activeCat !== "all" && s.category !== activeCat) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!s.name.toLowerCase().includes(q) && !s.short_summary.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [systems, search, activeCat]);

  return (
    <div className="pt-16">
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950 text-white" data-testid="hero-section">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/70 to-slate-950" />
        <div className="absolute inset-0 hero-grid opacity-40" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-28 sm:py-36 lg:py-44">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.22em] text-slate-300">
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
              Enterprise SaaS Showcase
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              Explore Our <span className="text-[#D4AF37]">Digital Solutions</span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed">
              A centralized showroom of enterprise-ready systems designed to scale businesses. Browse working products, unlock instant demos, and partner with us to deploy at speed.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                className="rounded-full bg-[#D4AF37] text-slate-900 hover:bg-[#F3CE52] gold-glow font-semibold h-12 px-7 text-base"
                data-testid="hero-browse-systems"
              >
                <a href="#systems">Browse Systems <ArrowRight className="w-4 h-4 ml-2" /></a>
              </Button>
              <Button
                onClick={() => setContactOpen(true)}
                variant="outline"
                className="rounded-full h-12 px-7 text-base text-white bg-white/5 hover:bg-white/10 hover:text-white border-white/20"
                data-testid="hero-request-demo"
              >
                Request Demo
              </Button>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 sm:gap-10 max-w-xl">
              {[
                { v: `${systems.length}+`, l: "Production Systems" },
                { v: "12", l: "Industries" },
                { v: "2-Click", l: "Demo Access" },
              ].map((m) => (
                <div key={m.l}>
                  <div className="font-heading text-2xl sm:text-3xl text-white font-bold">{m.v}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mt-1">{m.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section id="featured" className="max-w-7xl mx-auto px-6 lg:px-8 py-24" data-testid="featured-section">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" /> Featured
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 tracking-tight">Top performing systems</h2>
            </div>
            <a href="#systems" className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1.5">
              View all <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((s) => <SystemCard key={s.id} system={s} />)}
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section id="categories" className="bg-white border-y border-slate-200" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-2">
            <LayoutGrid className="w-3.5 h-3.5 text-[#2563EB]" /> Categories
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 tracking-tight">Built for every industry</h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CATEGORIES.map((c) => {
              const meta = CATEGORY_META[c];
              const Icon = meta.icon;
              const accentColor = meta.accent === "gold" ? "text-[#D4AF37]" : "text-[#2563EB]";
              const bg = meta.accent === "gold" ? "bg-[#D4AF37]/8" : "bg-[#2563EB]/8";
              return (
                <button
                  key={c}
                  onClick={() => { setActiveCat(c); document.getElementById("systems")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="group text-left p-5 rounded-2xl border border-slate-200 hover:border-slate-900 transition-all bg-white card-elev"
                  data-testid={`category-tile-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                >
                  <div className={`w-11 h-11 rounded-xl ${bg} grid place-items-center ${accentColor}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="mt-4 font-medium text-slate-900 leading-snug">{c}</div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ALL SYSTEMS */}
      <section id="systems" className="max-w-7xl mx-auto px-6 lg:px-8 py-24" data-testid="all-systems-section">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-[#D4AF37]" /> All Systems
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 tracking-tight">Pick a system and try it now</h2>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search systems..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 rounded-full border-slate-300"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Category chip filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          <button
            onClick={() => setActiveCat("all")}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.16em] font-semibold transition-colors ${
              activeCat === "all" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
            data-testid="filter-chip-all"
          >
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.16em] font-semibold transition-colors ${
                activeCat === c ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
              data-testid={`filter-chip-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="empty-systems">
            No systems match your filters.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((s) => <SystemCard key={s.id} system={s} />)}
          </div>
        )}
      </section>

      {/* CONTACT CTA */}
      <section id="contact" className="bg-slate-950 text-white" data-testid="contact-cta-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.22em] text-slate-300">
            <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> Trusted by 200+ teams
          </div>
          <h2 className="mt-6 font-heading text-3xl sm:text-5xl font-bold tracking-tighter">
            Let's deploy your next system <span className="text-[#D4AF37]">faster.</span>
          </h2>
          <p className="mt-5 text-slate-300 text-lg max-w-2xl mx-auto">
            Schedule a personalized walkthrough or request customization for your team. Our solution engineers respond within one business day.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => setContactOpen(true)}
              className="rounded-full h-12 px-7 bg-[#D4AF37] text-slate-900 hover:bg-[#F3CE52] font-semibold gold-glow"
              data-testid="contact-schedule-demo-btn"
            >
              Schedule Demo
            </Button>
            <Button
              onClick={() => setContactOpen(true)}
              variant="outline"
              className="rounded-full h-12 px-7 bg-white/5 text-white hover:bg-white/10 hover:text-white border-white/20"
              data-testid="contact-request-customization-btn"
            >
              Request Customization
            </Button>
          </div>
        </div>
      </section>

      <ContactRequestDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        requestType="schedule_demo"
      />
    </div>
  );
}
